/* 
    This is POI add/edit/delete 

    August 16, 2024 * Bryan Lilly
    
    Users can:
        -Add/edit/delete pois
            -on table
            -on map
        -see pois created by others and add them to their list or group
        
    *Pois are job specific
*/
import { useEffect, useState } from 'react';
//hooks
import useJobOptions from '../hooks/useJobOptions';
import useSiteMapRoute from '../hooks/useSiteMapRoute';
//components
import MapComponent from "../components/maps/MapComponent";
import { PageHeader } from '../components/ui/layout/PageHeader';
import { SelectContainer } from '../components/ui/layout/SelectContainer';
import { PageContainer } from '../components/ui/layout/PageContainer';
import Label from '../components/ui/labels/Label';
import Select from 'react-select';
import { TabContainer } from '../components/ui/tabNavigation/TabContainer';
import { TabButton } from '../components/ui/tabNavigation/TabButton';
import { PoiGroups } from "./pageComponents/poi/PoiGroups/PoiGroups"
import { PoiTable } from './pageComponents/poi/PoiTable/PoiTable';
//contexts
import { MapControlProvider } from '../contexts/MapControlContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { NotificationTable } from './pageComponents/notifications/NotificationTable';
import { useUser } from '@contexts/UserContext';
import { fetchJobData } from '@utils/jobs/fetchJobData';
import { useError } from '@contexts/ErrorContext';
import { MissingItemsIndicator } from '@components/ui/tabNavigation/MissingItemsIndicator';

/**
 * Parent component where users can create/read/update/delete POIs from a map. Data can be viewed and edited in the Table child component. POIs can be added to groups on the map or Groups component.
 * 
 * @returns {JSX.Element}
 * 
 * @category Pages
 */
export const Poi = () => {
    const { jobNameOptions } = useJobOptions();
    const { fetchRouteArr } = useSiteMapRoute();

    const [selectedJob, setSelectedJob] = useState(null);
    const [storedPoiGroup, setStoredPoiGroup] = useState(null); //if group selected, keep at top level, so it can be passed between components

    const [gglKey, setGglKey] = useState(1);
    const [activeComponent, setActiveComponent] = useState('Map');
    const [openPoiOnMap, setOpenPoiOnMap] = useState(null)

    const {hasUnsavedChanges, requestConfirm} = useUnsavedChanges();
    const {storedJob, setStoredJob, userPermissions} = useUser();

    const hasJobPermissions = userPermissions.jobs.create && userPermissions.jobs.update

    const {showError} = useError();

    //change component depending on what tab is selected
    const componentMap = {
        Map: <MapControlProvider>
                <MapComponent
                    key={gglKey}
                    selectedJob={selectedJob}
                    liveWPig={false}
                    poiMap={true}
                    openPoiOnMap={openPoiOnMap}
                    storedPoiGroup={storedPoiGroup}
                    setStoredPoiGroup={setStoredPoiGroup}
                />
            </MapControlProvider>,
        Table: <PoiTable 
                    selectedJob={selectedJob}
                    setOpenPoiOnMap={setOpenPoiOnMap}
                    setActiveComponent={setActiveComponent}
                    storedPoiGroup={storedPoiGroup}
                    setStoredPoiGroup={setStoredPoiGroup}
                />,
        PoiGroups: <PoiGroups 
                    selectedJob={selectedJob}
                    storedPoiGroup={storedPoiGroup}
                    setStoredPoiGroup={setStoredPoiGroup}
                />,
        NotificationTable:  <NotificationTable 
                                selectedJob={selectedJob}
                                setOpenPoiOnMap={setOpenPoiOnMap}
                                setActiveComponent={setActiveComponent}
                            />
    }
    
    //when selectedJob changes, get data for map
    useEffect(() => {
        setOpenPoiOnMap(null)
        if (selectedJob) {
            setGglKey(gglKey + 1);
            fetchRouteArr(selectedJob.value.toString());
        }
    }, [selectedJob]); //eslint-disable-line

    const handleJobSelected = (job) => {
        fetchJobData(job, showError, setSelectedJob, setStoredJob)
        setStoredPoiGroup(null)
    }

    const handleNavigation = async (component) => {
        if(component === activeComponent) return

        //reset openPoiOnMap - should only have a value when clicking map button in table
        setOpenPoiOnMap(null)

        //check for unsaved work on map
        //may want to update this to included unsaved work on table once it's capable of editting
        if(hasUnsavedChanges){
            const isConfirmed = await requestConfirm(`There is unsaved work on POI ${activeComponent === "PoiGroups" ? "Groups" : activeComponent} - are you sure you want to cancel?`)

            if(!isConfirmed) return
        }

        setActiveComponent(component);

    };

    useEffect(() => {
        document.title = `MarTrak - POI`

        //if a job was previously selected and added to UserContext, pull it in here
        if(storedJob){
            fetchJobData(storedJob, showError, setSelectedJob, setStoredJob)
        }
    }, []); //eslint-disable-line

    return (
        <>
            <PageHeader pageTitle="Points of Interest" />

            <PageContainer>
                <Label>Select A Job</Label>

                <SelectContainer>
                    <Select
                        options={jobNameOptions}
                        onChange={handleJobSelected}
                        menuPosition='fixed'
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        value={selectedJob}
                    />
                </SelectContainer>
                
                <TabContainer>
                    <TabButton
                        onClick={() => handleNavigation('Map')}
                        isActive={activeComponent === 'Map'}
                    >
                        Map
                    </TabButton>
                    <TabButton
                        onClick={() => handleNavigation('Table')}
                        isActive={activeComponent === 'Table'}
                    >
                        Table
                    </TabButton>
                    <TabButton
                        onClick={() => handleNavigation('PoiGroups')}
                        isActive={activeComponent === 'PoiGroups'}
                    >
                        POI Groups
                    </TabButton>
                    <TabButton
                        onClick={() => handleNavigation('NotificationTable')}
                        isActive={activeComponent === 'NotificationTable'}
                    >
                        Notifications
                        <MissingItemsIndicator 
                            showIndicator={storedJob ? Object.values(storedJob.notificationCount).some(r => r.POINotificationCount === 0) : false}
                            tooltipLabel="You have not added POI Notifications for all runs yet"
                        />
                    </TabButton>
                </TabContainer>

            </PageContainer>
            <div className='mb-20'>

                {!selectedJob ? (
                    <div className='flex justify-center mt-10'>
                        <p className='text-purple-600 font-bold italic'>Select a job to view POIs...</p>
                    </div>
                ) : selectedJob && selectedJob.IsLocked === false ? (
                    <div className='flex justify-center mt-10'>
                        <p className='text-purple-600 font-bold italic'>
                            {hasJobPermissions ? 
                            'Job is unlocked. To view this page, go to Job Details and lock the job.' : 
                            'Job is still being set up. Please check back later.'}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* instructions for user */}
                        {activeComponent === 'Map' && (
                            <div className="p-3 bg-violet-50 border border-violet-200 text-sm text-violet-800 rounded-lg martrak-item-margin mb-12 max-w-max">
                                <i className="fa-solid fa-circle-exclamation mr-2"></i>
                                Click on the map to add a POI. To view your POIs on the Site Map and Site Map w/PIG pages, you must add them to a group. You can create a group in the POI dialog box, or in the POI Groups tab.
                            </div>
                        )}

                        {/* show component selected from tabs */}
                        {componentMap[activeComponent]}
                    
                    </>
                )}


            </div>
        </>
    )
}